import { useMutation } from "@apollo/client";
import React from "react";

// region Language
import { GlobalMessages, CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

// region Libraries
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// atoms
import { ActionDialog } from "src/atoms";
import { Actions } from "src/atoms/ActionDialog";

// graphql
import { TUserHandleActiveMutations, userHandleActive } from "src/store/graphql/groups/user/mutation.graphql";
import { UserType, UserMutationsUpdateArgs, ECommandResultStatus } from "src/store/graphql/helpers/types.graphql";
import { useNavigate } from "react-router-dom";

interface InactiveUserDialogProps {
    open: boolean;
    onClose: () => void;
    user: UserType | undefined;
}

const InactiveUserDialog: React.FC<InactiveUserDialogProps> = ({ open, onClose, user }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Mutations
  const [MutationHandleActiveUser] = useMutation<TUserHandleActiveMutations,
  UserMutationsUpdateArgs>(userHandleActive());

  /**
  * Handle active user user
  * @param id Id
  * @param active Active
  */
  const mutationHandleActiveUser = async (id: string, active: boolean) => {
    const mutation = await MutationHandleActiveUser({
      variables: {
        user: {
          id,
          active
        }
      }
    });

    return mutation.data?.user.handleActiveUser;
  };

  const handleActiveUser = async () => {
    try {
      const response = await mutationHandleActiveUser(user?.id ?? "", !user?.active);

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({
        duration: 6000,
        type: "success",
        title: t(user?.active ? CustomerMessages.activedUser : CustomerMessages.inactivedUser)
      });

      navigate(0);

      onClose();
    } catch (error) {
      addToast({
        type: "info",
        title: t(user?.active ? CustomerMessages.errorInactivatingUser : CustomerMessages.errorActivatingUser)
      });
    }
  };

  const inactiveUserActions: Actions[] = [
    {
      text: t(GlobalMessages.buttonCancel),
      action: onClose
    },
    {
      text: t(user?.active ? CustomerMessages.inactivateUserButton : CustomerMessages.activateUserButton),
      action: handleActiveUser
    }
  ];

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={t(user?.active ? CustomerMessages.inactivateUserTitle : CustomerMessages.activateUserTitle)}
      actions={inactiveUserActions}
    >
      <span>
        {t(user?.active ? CustomerMessages.inactivateUserText : CustomerMessages.activateUserText)
          .replace("<USER_NAME>", user?.fullName ?? "")}
      </span>
      <br />
      <span>{user?.active && t(GlobalMessages.reversibleAction)}.</span>
    </ActionDialog>
  );
};

export default InactiveUserDialog;